import { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PrintIcon from "@mui/icons-material/Print";
import logoMrPintores from "../resources/images/mrpintores-logo.png";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { PenIcon } from "../components/svgicons/SvgIcons";
import FancyTooltip from "../components/utils/FancyTooltip";
import FancyTablePagination from "../components/utils/FancyTablePagination";
import FancyPaper from "../components/FancyPaper";
import { properties } from "../utils/Properties_es";
import {
  VacationDetail,
  VacationImportResult,
  VacationListItem,
  VacationPayment,
  VacationRecord,
  deleteVacationRecord,
  getVacationDetail,
  getVacationList,
  getVacationPayment,
  importVacationExcel,
  saveVacationRecord,
} from "../services/vacaciones/VacacionesService";
import { useApp } from "../hooks/useApp";

dayjs.locale("es");

const ROWS_PER_PAGE = 10;
// Regla de negocio (07-jul-2026): solo se acepta "Vacaciones", "Diligencias" se descartó.
const VACATION_TYPES = ["Vacaciones"];
// Tope de días a restar según tipo de pago del colaborador:
//   Quincenal (Administrativo): 30 días/mes
//   Semanal   (Operativo):      26 días/mes
// Con paso de 0,5 para permitir medio día.
const MAX_DAYS_QUINCENAL = 30;
const MAX_DAYS_SEMANAL = 26;
const buildDaysOptions = (maxDays: number): number[] => {
  const options: number[] = [];
  for (let i = 0.5; i <= maxDays; i += 0.5) options.push(i);
  return options;
};

const formatCRC = (value: number) =>
  new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const formatDate = (date: string) => dayjs(date).format("D/MM/YYYY");
const formatDays = (n: number | undefined | null) => {
  if (n == null) return "0";
  return Number.isInteger(n) ? String(n) : n.toString().replace(".", ",");
};

const paymentTypeChip = (paymentType: string) => {
  const isSemanal = paymentType?.toUpperCase()?.includes("SEMANAL");
  return (
    <Chip
      label={paymentType}
      size="small"
      sx={{
        bgcolor: isSemanal ? "#C8E6C9" : "#D1C4E9",
        color: isSemanal ? "#1B5E20" : "#4527A0",
        fontWeight: 600,
        borderRadius: "12px",
        minWidth: 110,
      }}
    />
  );
};

const ReadOnlyField = ({
  label,
  value,
  flex = 1,
  minWidth = 160,
}: {
  label: string;
  value: string;
  flex?: number;
  minWidth?: number;
}) => (
  <Box sx={{ flex, minWidth, display: "flex", flexDirection: "column", gap: 0.75 }}>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#222" }}>{label}</Typography>
    <TextField
      value={value}
      InputProps={{ readOnly: true, notched: false }}
      inputProps={{ style: { padding: "10px 14px" } }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px !important",
          background: "#FFF",
          "& .MuiOutlinedInput-notchedOutline": {
            top: 0,
            borderColor: "#D9D9D9",
            borderRadius: "10px !important",
          },
          "& .MuiOutlinedInput-notchedOutline legend": { display: "none", width: 0, maxWidth: 0 },
        },
      }}
    />
  </Box>
);

const BalanceField = ({
  label,
  subtitle,
  value,
  highlight,
}: {
  label: string;
  subtitle: string;
  value: number | string;
  highlight?: "neutral" | "ok" | "primary";
}) => {
  const bg =
    highlight === "primary"
      ? "#E3F2FD"
      : highlight === "ok"
      ? "#FFF"
      : "#FFF";
  const labelColor = highlight === "ok" ? "#2E7D32" : "#222";

  return (
    <Box sx={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box>
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: labelColor }}>
          {label}
        </Typography>
        <Typography component="span" sx={{ fontSize: 13, color: "#666", ml: 0.5 }}>
          / {subtitle}
        </Typography>
      </Box>
      <TextField
        value={value}
        InputProps={{ readOnly: true, notched: false }}
        inputProps={{ style: { padding: "10px 14px", textAlign: "left" } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px !important",
            background: bg,
            "& .MuiOutlinedInput-notchedOutline": {
              top: 0,
              borderColor: highlight === "primary" ? "#90CAF9" : "#D9D9D9",
              borderRadius: "10px !important",
            },
            "& .MuiOutlinedInput-notchedOutline legend": { display: "none", width: 0, maxWidth: 0 },
          },
        }}
      />
    </Box>
  );
};

const ManagementVacaciones = () => {
  const theme = useTheme();
  const { authInfo, setErrorMsg: setAppError, setSuccessMsg } = useApp();

  /* list */
  const [preFilter, setPreFilter] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<VacationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  /* detail modal */
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<VacationDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [registerFormOpen, setRegisterFormOpen] = useState(false);
  const [formType, setFormType] = useState<string>(VACATION_TYPES[0]);
  const [formStart, setFormStart] = useState<dayjs.Dayjs | null>(null);
  const [formEnd, setFormEnd] = useState<dayjs.Dayjs | null>(null);
  const [formDaysToDeduct, setFormDaysToDeduct] = useState<number>(0.5);
  const [formDescription, setFormDescription] = useState("");
  const [saving, setSaving] = useState(false);

  /* delete confirm */
  const [recordToDelete, setRecordToDelete] = useState<VacationRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  /* payment modal */
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [payment, setPayment] = useState<VacationPayment | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);

  /* import modal */
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<VacationImportResult | null>(null);

  /* ── load list ── */
  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const data = await getVacationList(search);
      setRows(data);
      setPage(0);
    } catch (e: any) {
      setListError(e.message ?? "Error al cargar vacaciones");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleApplyFilter = () => setSearch(preFilter);
  const handleResetSearch = () => {
    setPreFilter("");
    setSearch("");
  };

  /* ── open detail modal ── */
  const handleOpenDetail = async (row: VacationListItem) => {
    setDetailOpen(true);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const data = await getVacationDetail(row.personId);
      setDetail(data);
    } catch (e: any) {
      setAppError && setAppError(e.message ?? "Error al cargar detalle");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    setRegisterFormOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormType(VACATION_TYPES[0]);
    setFormStart(null);
    setFormEnd(null);
    setFormDaysToDeduct(0.5);
    setFormDescription("");
  };

  /**
   * Al elegir las fechas se propone como días a restar la cantidad de días que
   * cubre el rango. Queda editable: si la salida incluye domingos o feriados
   * que no consumen saldo, se baja a mano.
   */
  const proponerDiasARestar = (
    inicio: dayjs.Dayjs | null,
    fin: dayjs.Dayjs | null
  ) => {
    if (!inicio || !fin || fin.isBefore(inicio)) return;
    const diasDelRango = fin.diff(inicio, "day") + 1;
    const tope =
      detail?.paymentType?.toUpperCase()?.includes("QUINCENAL")
        ? MAX_DAYS_QUINCENAL
        : MAX_DAYS_SEMANAL;
    setFormDaysToDeduct(Math.min(diasDelRango, tope));
  };

  const handleChangeStart = (v: dayjs.Dayjs | null) => {
    setFormStart(v);
    proponerDiasARestar(v, formEnd);
  };

  const handleChangeEnd = (v: dayjs.Dayjs | null) => {
    setFormEnd(v);
    proponerDiasARestar(formStart, v);
  };

  /* ── register new salida ── */
  const handleRegisterSalida = async () => {
    if (!detail || !formStart || !formEnd) {
      setAppError && setAppError("Debe completar las fechas");
      return;
    }
    if (formEnd.isBefore(formStart)) {
      setAppError && setAppError("La fecha fin no puede ser anterior a la fecha inicio");
      return;
    }
    if (formDaysToDeduct <= 0) {
      setAppError && setAppError("Debe seleccionar los días a restar (mínimo 0,5)");
      return;
    }
    setSaving(true);
    try {
      await saveVacationRecord({
        personId: detail.personId,
        startDate: formStart.format("YYYY-MM-DD"),
        endDate: formEnd.format("YYYY-MM-DD"),
        type: formType,
        daysToDeduct: formDaysToDeduct,
        description: formDescription || undefined,
        user: authInfo?.username ?? "",
      });
      setSuccessMsg && setSuccessMsg("Registro guardado");
      setRegisterFormOpen(false);
      resetForm();
      // recargar detalle e listado
      const refreshed = await getVacationDetail(detail.personId);
      setDetail(refreshed);
      await loadList();
    } catch (e: any) {
      setAppError && setAppError(e.message ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  /* ── delete salida ── */
  const handleConfirmDelete = async () => {
    if (!recordToDelete || !detail) return;
    setDeleting(true);
    try {
      await deleteVacationRecord(recordToDelete.id);
      setSuccessMsg && setSuccessMsg("Salida eliminada");
      setRecordToDelete(null);
      const refreshed = await getVacationDetail(detail.personId);
      setDetail(refreshed);
      await loadList();
    } catch (e: any) {
      setAppError && setAppError(e.message ?? "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  /* ── open payment modal from a record ── */
  const handleOpenPayment = async (record: VacationRecord) => {
    if (!detail) return;
    setPaymentOpen(true);
    setLoadingPayment(true);
    setPayment(null);
    try {
      const data = await getVacationPayment(detail.personId, record.id);
      setPayment(data);
    } catch (e: any) {
      setAppError && setAppError(e.message ?? "Error al calcular pago");
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleClosePayment = () => {
    setPaymentOpen(false);
    setPayment(null);
  };

  /* ── boleta de solicitud ── */
  const handlePrintBoleta = (record: VacationRecord) => {
    if (!detail) return;

    const dias = record.daysToDeduct ?? record.days;
    // "Saldo actual" son los días que le quedan disponibles YA restados los
    // que va a salir (confirmado con el cliente). El saldo que devuelve el
    // back ya tiene descontado este registro, así que se usa tal cual.
    const saldoRestante = detail.availableDays;

    const hoy = dayjs();
    const periodo =
      record.startDate === record.endDate
        ? formatDate(record.startDate)
        : `${formatDate(record.startDate)} al ${formatDate(record.endDate)}`;

    const logoUrl = window.location.origin + logoMrPintores;

    // Réplica de "Boletas de solicitud de vacaciones al corte.docx".
    // El original es una tabla de 3 filas (encabezado / banda roja / firmas)
    // repetida dos veces por hoja: una copia para el colaborador y otra para
    // la empresa. Tipografía Bookman Old Style 12pt, banda #990000.
    const copia = () => `
      <table class="boleta">
        <tr><td class="bloque">
          <img src="${logoUrl}" class="logo" alt="M. R. Pintores S. A." />
          <div class="fecha"><b>FECHA</b>: ${hoy.format("DD")}/${hoy.format("MM")}/${hoy.format("YYYY")}</div>
          <div class="titulo"><b>BOLETA DE SOLICITUD DE VACACIONES</b></div>
          <div class="campos">
            <p class="negrita">NOMBRE: <span class="dato larga">${detail.fullName}</span>.</p>
            <p><b>SOLICITO</b>: <span class="dato corta">${formatDays(dias)}</span>
               día (s) de vacaciones del: <span class="dato media">${periodo}</span>.</p>
            <p>Saldo actual <span class="dato corta">${formatDays(saldoRestante)}</span>.</p>
          </div>
        </td></tr>
        <tr><td class="banda">Firma Persona Trabajadora</td></tr>
        <tr><td class="bloque firmas">
          <table class="grilla-firmas">
            <tr>
              <td><div class="raya"></div><b>Persona Trabajadora</b></td>
              <td>
                <div class="raya"></div>
                <b>Persona que autoriza</b><br/>
                <b>M. R. Pintores S. A.</b>
              </td>
            </tr>
          </table>
        </td></tr>
      </table>`;

    const html = `
      <!DOCTYPE html><html lang="es"><head>
      <meta charset="UTF-8"/>
      <title>Boleta de solicitud de vacaciones - ${detail.fullName}</title>
      <style>
        @page { margin: 12mm; }
        body {
          font-family: "Bookman Old Style", "URW Bookman L", Bookman, Georgia, serif;
          font-size: 12pt; color: #000; margin: 0;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        .boleta {
          width: 100%; border-collapse: collapse;
          table-layout: fixed; margin-bottom: 14px;
          page-break-inside: avoid;
        }
        .boleta > tr > td, .boleta td.bloque, .boleta td.banda {
          border: 0.5pt solid #000;
        }
        .bloque { padding: 10px 14px 14px; position: relative; vertical-align: top; }
        .logo { width: 3.72cm; height: auto; float: left; margin: 0 10px 4px 0; }
        .fecha { text-align: right; }
        .titulo { text-align: center; margin: 14px 0 22px; }
        .campos { clear: both; }
        .campos p { margin: 0 0 10px; }
        .campos p.negrita { font-weight: bold; }
        /* Los espacios en blanco del original se rellenan con el dato, pero se
           mantiene la línea inferior para que la boleta se lea igual. */
        .dato {
          display: inline-block; border-bottom: 1px solid #000;
          padding: 0 4px; text-align: center;
        }
        .dato.larga { min-width: 62%; }
        .dato.media { min-width: 38%; }
        .dato.corta { min-width: 68px; }
        .banda {
          background: #990000 !important; color: #FFFFFF;
          padding: 2px 8px;
        }
        .firmas { padding-top: 46px; padding-bottom: 10px; }
        .grilla-firmas {
          width: 82%; margin: 0 auto; border-collapse: collapse;
          table-layout: fixed;
        }
        .grilla-firmas td {
          border: 0; text-align: center; vertical-align: top;
          padding: 0 10px; line-height: 1.35;
        }
        .raya { border-top: 1px solid #000; margin-bottom: 4px; }
      </style>
      </head><body>
        ${copia()}
        ${copia()}
      </body></html>`;

    const win = window.open("", "_blank", "width=850,height=900");
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 600);
    }
  };

  /* ── import excel ── */
  const handleOpenImport = () => {
    setImportOpen(true);
    setImportFile(null);
    setImportResult(null);
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      setAppError && setAppError("Debe seleccionar un archivo Excel");
      return;
    }
    setImporting(true);
    try {
      const result = await importVacationExcel(
        importFile,
        authInfo?.username ?? ""
      );
      setImportResult(result);
      setSuccessMsg &&
        setSuccessMsg(
          `Importación finalizada: ${result.imported} de ${result.totalRows} colaboradores` +
            ` y ${result.monthlyRowsImported} meses de salario cargados`
        );
      await loadList();
    } catch (e: any) {
      setAppError && setAppError(e.message ?? "Error al importar Excel");
    } finally {
      setImporting(false);
    }
  };

  const pagedRows = rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage - 1);
  const handleChangeRowsPerPage = () => setPage(0);

  return (
    <FancyPaper pagetitle="Vacaciones">
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        {/* ── Barra superior: botón importar + buscador ── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pb: 6, flexWrap: "wrap", gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadFileIcon />}
            onClick={handleOpenImport}
            sx={{ borderRadius: "8px", py: 1.2 }}
          >
            Importar saldo inicial
          </Button>
        {/* ── Buscador ── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            sx={{
              background: theme.palette.secondary.light,
              borderRadius: "10px",
              padding: "15px 25px",
              width: { xs: "100%", md: "fit-content" },
            }}
          >
            <TextField
              placeholder="Buscar Empleado"
              sx={{
                width: { xs: "100%", md: 360 },
                marginRight: "10px",
                "& .MuiInputBase-root": { borderRadius: "18px", background: "#FFF" },
                "& input": { padding: "0.75rem 16px!important" },
                "& fieldset": { borderColor: "#FFF!important" },
              }}
              value={preFilter}
              onChange={(e: any) => {
                setPreFilter(e.target.value);
                if (e.target.value === "") handleResetSearch();
              }}
              onKeyDown={(e) => e.key === "Enter" && handleApplyFilter()}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={handleApplyFilter}
              sx={{
                borderRadius: "8px!important",
                padding: "0.75rem 2rem",
                background: "#FFF",
                whiteSpace: "nowrap",
              }}
            >
              Buscar
            </Button>
          </Stack>
        </Box>
        </Box>

        {listError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {listError}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="vacaciones table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Colaborador</TableCell>
                    <TableCell align="center">Cédula</TableCell>
                    <TableCell align="center">Cargo</TableCell>
                    <TableCell align="center">Días Disponibles</TableCell>
                    <TableCell align="center">Tipo Pago</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.length > 0 ? (
                    pagedRows.map((row, idx) => (
                      <TableRow
                        key={`${row.personId}-${idx}`}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell align="center">{row.fullName}</TableCell>
                        <TableCell align="center">{row.dni}</TableCell>
                        <TableCell align="center">{row.positionName}</TableCell>
                        <TableCell align="center">{formatDays(row.availableDays)} días</TableCell>
                        <TableCell align="center">{paymentTypeChip(row.paymentType)}</TableCell>
                        <TableCell align="center">
                          <FancyTooltip title="Ver detalle" placement="top">
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenDetail(row)}
                            >
                              <PenIcon />
                            </IconButton>
                          </FancyTooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {properties.com_parval_label_table_empty}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <FancyTablePagination
              count={pagedRows.length}
              rowsPerPage={ROWS_PER_PAGE}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              totalElements={rows.length}
              totalPages={Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE))}
            />
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            DETAIL DIALOG
        ═══════════════════════════════════════════════════════════════════════ */}
        <Dialog
          open={detailOpen}
          onClose={(_e, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") return;
            handleCloseDetail();
          }}
          disableEscapeKeyDown
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { maxHeight: "92vh" } }}
        >
          <DialogTitle sx={{ pt: 6, px: 5 }}>
            <Typography
              variant="h6"
              color="primary"
              sx={{ fontWeight: 700, fontSize: "1.2rem" }}
            >
              Detalle de Vacaciones
            </Typography>
            <Divider sx={{ mt: 2, borderBottomWidth: 2, borderColor: "#000" }} />
          </DialogTitle>
          <DialogContent sx={{ px: 5, pt: 3 }}>
            {loadingDetail ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : detail ? (
              <>
                {/* Header info */}
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 2, mb: 4 }}>
                  <ReadOnlyField label="Colaborador" value={detail.fullName} flex={2} minWidth={220} />
                  <ReadOnlyField label="Cargo" value={detail.positionName} />
                  <ReadOnlyField
                    label="Tipo de Pago"
                    value={`${detail.paymentType}${
                      detail.paymentType?.toUpperCase() === "QUINCENAL"
                        ? " (350 días)"
                        : detail.paymentType?.toUpperCase() === "SEMANAL"
                        ? " (300 días)"
                        : ""
                    }`}
                  />
                  <ReadOnlyField
                    label="Salario Mensual"
                    value={formatCRC(detail.monthlySalary)}
                  />
                </Box>

                {/* Detalle por mes */}
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 1 }}
                >
                  Detalle por mes
                </Typography>
                <Divider sx={{ mb: 3, borderBottomWidth: 2, borderColor: "#000" }} />
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 5 }}>
                  <BalanceField
                    label="Acumulados"
                    subtitle="Años anteriores"
                    value={detail.accumulatedDays}
                  />
                  <BalanceField
                    label="Disfrutados"
                    subtitle="Días ya tomados"
                    value={detail.enjoyedDays}
                    highlight="ok"
                  />
                  <BalanceField
                    label="Saldo Neto"
                    subtitle="Disponibles"
                    value={detail.availableDays}
                    highlight="primary"
                  />
                </Box>

                {/* Historial */}
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 1 }}
                >
                  Historial de Salidas
                </Typography>
                <Divider sx={{ mb: 2, borderBottomWidth: 2, borderColor: "#000" }} />

                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setRegisterFormOpen((v) => !v)}
                    sx={{ color: "primary.main", textTransform: "none", fontWeight: 600 }}
                  >
                    Registrar Salida
                  </Button>
                </Box>

                {/* Form colapsable */}
                {registerFormOpen && (
                  <Paper
                    variant="outlined"
                    sx={{ p: 3, mb: 3, borderRadius: 2, background: "#FAFAFA" }}
                  >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                      <DatePicker
                        label="Fecha Inicio"
                        value={formStart}
                        onChange={handleChangeStart}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            sx: { flex: 1, minWidth: 200 },
                            InputLabelProps: { shrink: true },
                          },
                        }}
                      />
                      <DatePicker
                        label="Fecha Fin"
                        value={formEnd}
                        onChange={handleChangeEnd}
                        format="DD/MM/YYYY"
                        minDate={formStart ?? undefined}
                        slotProps={{
                          textField: {
                            sx: { flex: 1, minWidth: 200 },
                            InputLabelProps: { shrink: true },
                          },
                        }}
                      />
                    </Stack>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                      <Autocomplete
                        disableClearable
                        options={VACATION_TYPES}
                        value={formType}
                        onChange={(_e, v) => v && setFormType(v)}
                        sx={{ flex: 1, minWidth: 200 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Tipo"
                            placeholder="Seleccione el tipo"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                      <Autocomplete
                        disableClearable
                        options={buildDaysOptions(
                          detail.paymentType?.toUpperCase()?.includes("QUINCENAL")
                            ? MAX_DAYS_QUINCENAL
                            : MAX_DAYS_SEMANAL
                        )}
                        getOptionLabel={(o) => formatDays(o)}
                        value={formDaysToDeduct}
                        onChange={(_e, v) => setFormDaysToDeduct(v ?? 0)}
                        sx={{ flex: 1, minWidth: 200 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Días a restar del saldo"
                            InputLabelProps={{ shrink: true }}
                            helperText={
                              formStart && formEnd && !formEnd.isBefore(formStart)
                                ? `El rango cubre ${formEnd.diff(formStart, "day") + 1} día(s). Ajustar si hay domingos o feriados.`
                                : "Se calcula al elegir las fechas"
                            }
                          />
                        )}
                      />
                    </Stack>
                    <TextField
                      label="Descripción"
                      placeholder="Motivo de la salida"
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: 2 }}
                    />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          setRegisterFormOpen(false);
                          resetForm();
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CalendarMonthIcon />}
                        onClick={handleRegisterSalida}
                        disabled={saving}
                      >
                        {saving ? <CircularProgress size={18} /> : "Registrar"}
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {/* Tabla historial */}
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha Inicio</TableCell>
                        <TableCell>Fecha Final</TableCell>
                        <TableCell align="center">Días</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.records.length > 0 ? (
                        detail.records.map((r, idx) => (
                          <TableRow
                            key={r.id}
                            sx={{ background: idx % 2 === 0 ? "#FBF6F2" : "transparent" }}
                          >
                            <TableCell>{formatDate(r.startDate)}</TableCell>
                            <TableCell>{formatDate(r.endDate)}</TableCell>
                            <TableCell align="center">{formatDays(r.daysToDeduct ?? r.days)}</TableCell>
                            <TableCell>{r.type}</TableCell>
                            <TableCell>{r.description ?? "-"}</TableCell>
                            <TableCell align="center">
                              <FancyTooltip title="Cálculo de vacaciones" placement="top">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenPayment(r)}
                                >
                                  <PenIcon />
                                </IconButton>
                              </FancyTooltip>
                              <FancyTooltip title="Imprimir boleta" placement="top">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handlePrintBoleta(r)}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </FancyTooltip>
                              <FancyTooltip title="Eliminar" placement="top">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => setRecordToDelete(r)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </FancyTooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            Sin salidas registradas
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 5, py: 3, gap: 1 }}>
            <Button variant="outlined" color="primary" onClick={handleCloseDetail}>
              Volver
            </Button>
            <Button variant="contained" color="primary" onClick={handleCloseDetail}>
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════════════
            DELETE CONFIRMATION
        ═══════════════════════════════════════════════════════════════════════ */}
        <Dialog
          open={recordToDelete != null}
          onClose={() => setRecordToDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: "primary.main", textAlign: "center" }}>
            ¿Cancelar esta Vacación?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ textAlign: "center" }}>
              Al eliminar esta salida, los días se reintegrarán de inmediato al saldo disponible
              y el monto será removido de la planilla semanal. Esta acción es definitiva.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1, justifyContent: "center" }}>
            <Button variant="outlined" color="primary" onClick={() => setRecordToDelete(null)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={18} /> : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════════════
            PAYMENT MODAL
        ═══════════════════════════════════════════════════════════════════════ */}
        <Dialog
          open={paymentOpen}
          onClose={handleClosePayment}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { maxHeight: "92vh" } }}
        >
          <DialogTitle sx={{ pt: 6, px: 5 }}>
            <Typography
              variant="h6"
              color="primary"
              sx={{ fontWeight: 700, fontSize: "1.2rem" }}
            >
              Cálculo de Vacaciones
            </Typography>
            <Divider sx={{ mt: 2, borderBottomWidth: 2, borderColor: "#000" }} />
          </DialogTitle>
          <DialogContent sx={{ px: 5, pt: 3 }}>
            {loadingPayment ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : payment ? (
              <>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 2, mb: 4 }}>
                  <ReadOnlyField label="Colaborador" value={payment.fullName} flex={2} minWidth={220} />
                  <ReadOnlyField label="Cargo" value={payment.positionName} />
                  <Box sx={{ flex: 1, minWidth: 160 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#222", mb: 1 }}>
                      Tipo de pago
                    </Typography>
                    <Box sx={{ pt: 1 }}>{paymentTypeChip(
                      `${payment.paymentType}${
                        payment.paymentType?.toUpperCase() === "QUINCENAL"
                          ? " (350 días)"
                          : payment.paymentType?.toUpperCase() === "SEMANAL"
                          ? " (300 días)"
                          : ""
                      }`
                    )}</Box>
                  </Box>
                </Box>

                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 1 }}
                >
                  Resumen de Saldos
                </Typography>
                <Divider sx={{ mb: 3, borderBottomWidth: 2, borderColor: "#000" }} />

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mes</TableCell>
                        <TableCell align="right">Salario</TableCell>
                        <TableCell align="center">Cantidad de días laborados</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payment.months.map((m, idx) => (
                        <TableRow
                          key={`${m.year}-${m.month}-${idx}`}
                          sx={{ background: idx % 2 === 0 ? "#FBF6F2" : "transparent" }}
                        >
                          <TableCell>{m.monthLabel}</TableCell>
                          <TableCell align="right">{formatCRC(m.salary)}</TableCell>
                          <TableCell align="center">{m.daysWorked}</TableCell>
                        </TableRow>
                      ))}
                      {/* Fila de totales: va en el cuerpo, así que el peso y el
                          color se ponen a mano (el tema solo los aplica a los
                          encabezados). */}
                      <TableRow
                        sx={{
                          "& td": {
                            fontWeight: 700,
                            color: "#222",
                            borderTop: "2px solid #E0E0E0",
                            background: "#F5F5F5",
                          },
                        }}
                      >
                        <TableCell>TOTAL</TableCell>
                        <TableCell align="right">{formatCRC(payment.totalSalary12Months)}</TableCell>
                        <TableCell align="center">{payment.totalDaysWorked}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Resumen horizontal */}
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Salario promedio diario</TableCell>
                        <TableCell align="center">Días de vacaciones que tomará</TableCell>
                        <TableCell align="center">Subtotal vacaciones</TableCell>
                        <TableCell align="center">Deducción CCSS</TableCell>
                        <TableCell align="right">Total a pagar</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ "& td": { fontWeight: 600, color: "#222" } }}>
                        <TableCell>{formatCRC(payment.dailySalaryAverage)}</TableCell>
                        <TableCell align="center">{formatDays(payment.vacationDays)}</TableCell>
                        <TableCell align="center">{formatCRC(payment.subtotalVacations)}</TableCell>
                        <TableCell align="center">{formatCRC(payment.ccssDeduction)}</TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              bgcolor: "#E3F2FD",
                              border: "1px solid #90CAF9",
                              borderRadius: 1,
                              px: 2,
                              py: 1,
                              display: "inline-block",
                              fontWeight: 700,
                              color: "#1565C0",
                            }}
                          >
                            {formatCRC(payment.totalToPay)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 5, py: 3, gap: 1 }}>
            <Button variant="contained" color="primary" onClick={handleClosePayment}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════════════════════════════════════════════════
            IMPORT EXCEL DIALOG (Saldo inicial de años anteriores)
        ═══════════════════════════════════════════════════════════════════ */}
        <Dialog
          open={importOpen}
          onClose={handleCloseImport}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pt: 4, px: 4 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              Importar saldo inicial de vacaciones
            </Typography>
            <Divider sx={{ mt: 2, borderBottomWidth: 2, borderColor: "#000" }} />
          </DialogTitle>
          <DialogContent sx={{ px: 4, pt: 3 }}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: "#FAFAFA",
                border: "1px solid #E0E0E0",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#222", mb: 1 }}>
                Formato esperado del archivo
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.75 } }}>
                <Typography component="li" sx={{ fontSize: 13, color: "#444" }}>
                  Dos hojas llamadas{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#222" }}>
                    Operarios
                  </Box>{" "}
                  y{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#222" }}>
                    Administrativos
                  </Box>
                  , con las mismas columnas.
                </Typography>
                <Typography component="li" sx={{ fontSize: 13, color: "#444" }}>
                  Columnas fijas: Nombre del colaborador, Días acumulados, Días
                  disfrutados y Saldo días.
                </Typography>
                <Typography component="li" sx={{ fontSize: 13, color: "#444" }}>
                  A partir de la quinta columna, pares de{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#222" }}>
                    Salario &lt;mes&gt; &lt;año&gt;
                  </Box>{" "}
                  y{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#222" }}>
                    Cantidad días &lt;mes&gt; &lt;año&gt;
                  </Box>
                  , en cualquier cantidad de meses.
                </Typography>
                <Typography component="li" sx={{ fontSize: 13, color: "#444" }}>
                  Los nombres se comparan sin distinguir mayúsculas ni tildes.
                  Los meses sin datos no se toman en cuenta.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
              >
                Seleccionar archivo Excel
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                />
              </Button>
              {importFile && (
                <Typography variant="body2" sx={{ color: "#333" }}>
                  {importFile.name}
                </Typography>
              )}
            </Box>

            {importResult && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Resumen de importación
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
                  <Chip
                    label={`Total filas: ${importResult.totalRows}`}
                    sx={{ bgcolor: "#E3F2FD" }}
                  />
                  <Chip
                    label={`Colaboradores importados: ${importResult.imported}`}
                    sx={{ bgcolor: "#C8E6C9", color: "#1B5E20", fontWeight: 600 }}
                  />
                  <Chip
                    label={`Meses de salario cargados: ${importResult.monthlyRowsImported}`}
                    sx={{ bgcolor: "#C8E6C9", color: "#1B5E20", fontWeight: 600 }}
                  />
                  <Chip
                    label={`Errores: ${importResult.errors.length}`}
                    sx={{
                      bgcolor: importResult.errors.length > 0 ? "#FFCDD2" : "#EEEEEE",
                      color: importResult.errors.length > 0 ? "#B71C1C" : "#333",
                      fontWeight: 600,
                    }}
                  />
                  {importResult.warnings.length > 0 && (
                    <Chip
                      label={`Advertencias: ${importResult.warnings.length}`}
                      sx={{ bgcolor: "#FFE0B2", color: "#E65100", fontWeight: 600 }}
                    />
                  )}
                </Stack>

                {importResult.errors.length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#B71C1C", mb: 1 }}>
                      Filas que no se importaron
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 240, mb: 3 }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Hoja</TableCell>
                            <TableCell align="center">Fila</TableCell>
                            <TableCell>Colaborador</TableCell>
                            <TableCell>Motivo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importResult.errors.map((err, i) => (
                            <TableRow key={i}>
                              <TableCell>{err.sheet}</TableCell>
                              <TableCell align="center">{err.row}</TableCell>
                              <TableCell>{err.fullName ?? "-"}</TableCell>
                              <TableCell sx={{ color: "#B71C1C" }}>{err.message}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {importResult.warnings.length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#E65100", mb: 0.5 }}>
                      Advertencias
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 1 }}>
                      Estas filas sí se importaron, pero conviene revisarlas.
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 240 }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Hoja</TableCell>
                            <TableCell align="center">Fila</TableCell>
                            <TableCell>Colaborador</TableCell>
                            <TableCell>Detalle</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importResult.warnings.map((w, i) => (
                            <TableRow key={i}>
                              <TableCell>{w.sheet}</TableCell>
                              <TableCell align="center">{w.row}</TableCell>
                              <TableCell>{w.fullName ?? "-"}</TableCell>
                              <TableCell sx={{ color: "#E65100" }}>{w.message}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3, gap: 1 }}>
            <Button variant="outlined" color="primary" onClick={handleCloseImport}>
              Cerrar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleImportSubmit}
              disabled={importing || !importFile}
            >
              {importing ? <CircularProgress size={18} /> : "Importar"}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </FancyPaper>
  );
};

export default ManagementVacaciones;
